// Presentation helpers for the federation & coordination surfaces. Human-first
// language only — the backend remains authoritative for all authorization.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

export const REL_TYPE_LABEL = {
  area_of: "Area of", region_of: "Region of", district_of: "District of",
  township_of: "Township of", ward_of: "Ward of", chapter_of: "Chapter of",
  coalition_peer: "Coalition partner", collaborates_with: "Collaborates with",
  campaign_affiliated: "Campaign affiliated", club_recognized: "Club recognized by",
};

export const REL_STATUS = {
  pending_acceptance: { label: "Pending", variant: "vision" },
  active: { label: "Connected", variant: "implemented" },
  suspended: { label: "Paused", variant: "vision" },
  terminated: { label: "Ended", variant: "neutral" },
  rejected: { label: "Declined", variant: "neutral" },
};

export const GRANT_STATUS = {
  offered: { label: "Pending", variant: "vision" },
  active: { label: "Active", variant: "implemented" },
  revoked: { label: "Revoked", variant: "neutral" },
  expired: { label: "Expired", variant: "neutral" },
  suspended: { label: "Paused", variant: "vision" },
};

export const GROUP_STATUS = {
  active: { label: "Active", variant: "implemented" },
  paused: { label: "Paused", variant: "vision" },
  completed: { label: "Completed", variant: "info" },
  dissolved: { label: "Dissolved", variant: "neutral" },
  archived: { label: "Archived", variant: "neutral" },
};

export const PART_STATUS = {
  invited: { label: "Invited", variant: "vision" },
  active: { label: "Active", variant: "implemented" },
  declined: { label: "Declined", variant: "neutral" },
  withdrawn: { label: "Withdrew", variant: "neutral" },
  removed: { label: "Removed", variant: "neutral" },
};

export const LEVEL_LABEL = {
  read_only: "View only", contribute: "Contribute", manage: "Manage",
  aggregate_only: "Summaries only",
};

export const orgLabel = (item, fallback = "Partner organization") =>
  item?.counterpart_name || item?.display_label || fallback;

export const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

export function apiError(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  const map = {
    target_organization_not_found: "That organization ID doesn't match an active organization.",
    cannot_relate_to_self: "You can't create a relationship with your own organization.",
    relationship_exists: "A relationship of this type already exists with that organization.",
    would_create_cycle: "That would create a circular relationship.",
    no_active_relationship: "That relationship is no longer active.",
    accessor_not_active_participant: "That organization isn't an active participant.",
    not_a_representative: "You aren't a representative for this organization in this effort.",
    membership_ended: "Your membership in this organization has ended.",
    already_invited_or_active: "That organization is already invited or participating.",
  };
  toast.error(map[d] || fallback);
}

// Data hook: load + reload with loading/denied state.
export function useLoader(fn, deps) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fn();
      setData(r);
      setDenied(false);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { load(); }, [load]);
  return { data, loading, denied, reload: load, setData };
}

// A simple presentation-only lexical rank between two keys (fractional index).
export function rankBetween(a, b) {
  const A = a || "", B = b || "";
  if (!A && !B) return "n";
  if (!A) return String.fromCharCode(Math.max(98, (B.charCodeAt(0) || 110) - 1));
  if (!B) return A + "n";
  // midpoint of first differing char, else append
  if (A < B) {
    const mid = Math.floor(((A.charCodeAt(0) || 97) + (B.charCodeAt(0) || 122)) / 2);
    if (mid > (A.charCodeAt(0) || 97) && mid < (B.charCodeAt(0) || 122)) return String.fromCharCode(mid);
    return A + "n";
  }
  return A + "n";
}
